import { formatUnits } from 'viem'
import {
  apyToApr,
  calculateRateForCompoundType,
  TimeScale,
} from '../../../../utils/parsing'
import { CAction } from './types'

// ---- Types that mirror your Solidity structs ----
export interface MarketIncentives {
  token: string // address
  supplyIncentivesPerSec: bigint // uint -> BigInt
  borrowIncentivesPerSec: bigint // uint -> BigInt
}

export interface Market {
  market: string // address
  isListed: boolean
  borrowCap: number // uint -> number
  supplyCap: number // uint -> number
  // mintPaused: boolean
  // isBorrowAllowed: boolean
  collateralFactorMantissa: bigint // uint -> BigInt
  underlyingPrice: bigint // uint -> BigInt
  totalSupply: bigint // uint -> BigInt
  totalBorrows: bigint // uint -> BigInt
  totalReserves: bigint // uint -> BigInt
  cash: bigint // uint -> BigInt
  exchangeRateCurrent: bigint // uint -> BigInt
  borrowIndex: bigint // uint -> BigInt
  reserveFactor: bigint // uint -> BigInt
  // borrowRate: bigint // uint -> BigInt
  // supplyRate: bigint // uint -> BigInt
  incentives: MarketIncentives[]
  pausedActions: { [a: number]: boolean }
  depositRate: number
  variableBorrowRate: number
  isBorrowAllowed: boolean
  cTokenDecimals: number
}

// ---- Helpers: tolerant conversions for common on-chain shapes ----
function toBigInt(value: unknown): bigint {
  if (typeof value === 'bigint') return value
  if (typeof value === 'number') return BigInt(value) // assumes safe range (RPC numbers are rare)
  if (typeof value === 'string') return BigInt(value) // supports "123" or "0x..."
  if (value && typeof value === 'object') {
    const v = value as any
    // ethers v5 BigNumber: {_hex: "0x..."}
    if (typeof v._hex === 'string') return BigInt(v._hex)
    // ethers v6 BigInt-ish: .toString() exists
    if (typeof v.toString === 'function') return BigInt(v.toString())
    // viem-style: .toBigInt?
    if (typeof v.toBigInt === 'function') return v.toBigInt()
  }
  throw new Error(`Cannot convert value to bigint: ${String(value)}`)
}

function toBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'bigint') return value !== 0n
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase()
    if (s === 'true' || s === '1') return true
    if (s === 'false' || s === '0') return false
  }
  // Fallback: treat non-nullish truthy as true
  return Boolean(value)
}

function toAddress(value: unknown): string {
  if (typeof value === 'string') return value // don't checksum here; keep raw
  // ethers/viem often provide .toString()
  if (value && typeof (value as any).toString === 'function')
    return (value as any).toString()
  throw new Error(`Cannot convert value to address string: ${String(value)}`)
}

// Utility to read either named fields or tuple positions.
// Example: field(raw, "market", 0) will try raw.market then raw[0].
function field<T = unknown>(src: any, key: string, index: number): T {
  if (src == null) throw new Error('Source is null/undefined')
  if (key in src) return src[key]
  if (Array.isArray(src)) return src[index]
  return (src as any)[key]
}

// ---- Parsers ----
export function parseMarketIncentives(input: any): MarketIncentives {
  return {
    token: toAddress(field(input, 'token', 0)),
    supplyIncentivesPerSec: toBigInt(field(input, 'supplyIncentivesPerSec', 1)),
    borrowIncentivesPerSec: toBigInt(field(input, 'borrowIncentivesPerSec', 2)),
  }
}

export function parseMoonwellMarket(input: any, token: any): Market {
  const incentivesRaw: any[] = field<any[]>(input, 'incentives', 18) ?? [] // array of MarketIncentives (objects or tuples)

  return {
    market: toAddress(field(input, 'market', 0)),
    cTokenDecimals: 8,
    isListed: toBool(field(input, 'isListed', 1)),
    // borrowCap: toBigInt(field(input, 'borrowCap', 2)),
    // supplyCap: toBigInt(field(input, 'supplyCap', 3)),
    // mintPaused: toBool(field(input, 'mintPaused', 4)),
    // isBorrowAllowed: !toBool(field(input, 'borrowPaused', 5)),
    collateralFactorMantissa: toBigInt(field(input, 'collateralFactor', 6)),
    underlyingPrice: toBigInt(field(input, 'underlyingPrice', 7)),
    totalSupply: toBigInt(field(input, 'totalSupply', 8)),
    totalBorrows: toBigInt(field(input, 'totalBorrows', 9)),
    totalReserves: toBigInt(field(input, 'totalReserves', 10)),
    cash: toBigInt(field(input, 'cash', 11)),
    exchangeRateCurrent: toBigInt(field(input, 'exchangeRate', 12)),
    borrowIndex: toBigInt(field(input, 'borrowIndex', 13)),
    reserveFactor: toBigInt(field(input, 'reserveFactor', 14)),
    // borrowRate: toBigInt(field(input, 'borrowRate', 15)),
    // supplyRate: toBigInt(field(input, 'supplyRate', 16)),
    pausedActions: {
      [CAction.MINT]: toBool(field(input, 'mintPaused', 4)),
      [CAction.BORROW]: toBool(field(input, 'borrowPaused', 5)),
    },
    incentives: (incentivesRaw ?? []).map(parseMarketIncentives),
    depositRate:
      apyToApr(
        calculateRateForCompoundType(
          toBigInt(field(input, 'supplyRate', 15)).toString() ?? '0',
          '1',
          TimeScale.SECOND,
        ) / 100,
      ) * 100,
    variableBorrowRate:
      apyToApr(
        calculateRateForCompoundType(
          toBigInt(field(input, 'borrowRate', 15)).toString() ?? '0',
          '1',
          TimeScale.SECOND,
        ) / 100,
      ) * 100,
    isBorrowAllowed: true,
    borrowCap: Number(
      formatUnits(toBigInt(field(input, 'borrowCap', 2)), token.decimals ?? 18),
    ),
    supplyCap: Number(
      formatUnits(toBigInt(field(input, 'supplyCap', 2)), token.decimals ?? 18),
    ),
  }
}

// ---- Optional: parse an array of markets ----
export function parseMarkets(inputs: any[]): Market[] {
  return inputs.map(parseMoonwellMarket)
}
